import { useCallback, useMemo, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'

import { describeError, fieldOfError } from '../lib/api/messages.ts'
import { companies, machines, productKeys } from '../lib/api/resources.ts'
import { PRODUCT_KEY_STATUSES } from '../lib/api/types.ts'
import type {
  CompanyResponse,
  MachineResponse,
  PagedResponse,
  ProductKeyResponse,
  ProductKeyStatus,
} from '../lib/api/types.ts'
import { licenseUninstallToast, licenseUninstallWarning } from '../lib/commands/uninstall.ts'
import { formatDayClock } from '../lib/format.ts'
import { navigate, useQueryParam } from '../lib/router.tsx'
import { useAction } from '../lib/useAction.ts'
import { useResource } from '../lib/useResource.ts'
import { PageHeader, Toolbar } from '../components/layout/PageHeader.tsx'
import { UninstallWarning } from '../components/machines/UninstallAgent.tsx'
import { Badge, ProductKeyStatusBadge, STATUS_LABEL } from '../components/primitives/Badge.tsx'
import { Button } from '../components/primitives/Button.tsx'
import { CopyValue, ShortId } from '../components/primitives/CopyValue.tsx'
import { ConfirmDialog, FormDialog } from '../components/primitives/Dialog.tsx'
import {
  CheckboxField,
  FilterToggle,
  SearchInput,
  SelectField,
} from '../components/primitives/Field.tsx'
import { Menu, MenuItem, MenuSeparator } from '../components/primitives/Menu.tsx'
import { Pagination } from '../components/primitives/Pagination.tsx'
import { EmptyState, ErrorState, LoadingRow, Notice } from '../components/primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../components/primitives/table.ts'
import { useToast } from '../components/primitives/Toast.tsx'

const PAGE_SIZE = 50

/**
 * Quantas empresas e máquinas são carregadas só para traduzir id em nome.
 *
 * É o teto do servidor. Acima disso a linha mostra o id abreviado em vez do
 * código — degrada, não mente. Resolver nome por linha custaria uma requisição
 * por célula, e um índice completo no cliente seria um cache que envelhece sem
 * ninguém perceber.
 */
const LABEL_LIMIT = 200

type Dialog =
  | { kind: 'create' }
  | { kind: 'activate'; key: ProductKeyResponse }
  | { kind: 'suspend'; key: ProductKeyResponse }
  | { kind: 'revoke'; key: ProductKeyResponse }
  | { kind: 'bind'; key: ProductKeyResponse }
  | { kind: 'unbind'; key: ProductKeyResponse }
  | { kind: 'delete'; key: ProductKeyResponse }
  | null

/**
 * Licenças.
 *
 * É a tela mais sensível do console: ativar uma licença autoriza uma máquina a
 * escrever telemetria dentro de uma empresa, e revogar corta a coleta de
 * segurança de um cliente.
 *
 * Toda mudança de estado passa por `/activate`, `/suspend` e `/revoke` — nunca
 * pelo `PUT`. O `PUT` tem semântica de substituição total: um formulário que
 * esquecesse `machineId` desvincularia a máquina como efeito colateral de mudar
 * o status. Ele é usado em exatamente dois lugares aqui (vincular e desvincular
 * máquina), e nos dois o `machineId` vai explícito.
 */
export function Licenses() {
  const companyFilter = useQueryParam('companyId') ?? ''
  const machineFilter = useQueryParam('machineId') ?? ''

  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<ProductKeyStatus | ''>('')
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [lookupInput, setLookupInput] = useState('')
  const [lookup, setLookup] = useState('')
  const [dialog, setDialog] = useState<Dialog>(null)

  const companyIndex = useCompanyIndex()
  const machineIndex = useMachineIndex()

  const fetcher = useCallback(
    async (signal: AbortSignal): Promise<PagedResponse<ProductKeyResponse>> => {
      // A busca por texto da chave é um caminho separado: ela tolera caixa e
      // hífens fora do padrão porque quem usa está colando de um e-mail ou de um
      // ticket, e devolve um recurso só.
      if (lookup !== '') {
        const found = await productKeys.lookup(lookup, signal)
        return { items: [found], page: 1, pageSize: 1, totalCount: 1, totalPages: 1 }
      }

      return productKeys.list(
        {
          page,
          pageSize: PAGE_SIZE,
          companyId: companyFilter,
          machineId: machineFilter,
          status: status === '' ? undefined : status,
          includeDeleted,
        },
        signal,
      )
    },
    [lookup, page, companyFilter, machineFilter, status, includeDeleted],
  )

  const { data, error, loading, reload } = useResource(fetcher)

  const close = useCallback(() => setDialog(null), [])
  const refresh = useCallback(() => {
    setDialog(null)
    reload()
  }, [reload])

  const submitLookup = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    setLookup(lookupInput.trim())
    setPage(1)
  }

  const filtered = companyFilter !== '' || machineFilter !== ''

  /**
   * A máquina da licença tem OUTRA licença viva (não removida, não revogada)?
   * Só com o que já está na tela: `true` se a viu, `false` se a tela mostra
   * todas as licenças da máquina, `null` se não dá para saber.
   */
  const otherLiveKeyOf = (key: ProductKeyResponse): boolean | null => {
    if (key.machineId === null || data === null) return null
    const seen = data.items.some(
      (other) =>
        other.id !== key.id &&
        other.machineId === key.machineId &&
        other.deletedAt === null &&
        other.status !== 'Revoked',
    )
    if (seen) return true
    const complete = lookup === '' && machineFilter === key.machineId && status === '' && data.totalPages <= 1
    return complete ? false : null
  }

  return (
    <>
      <PageHeader
        title="Licenças"
        actions={
          <Button variant="primary" onClick={() => setDialog({ kind: 'create' })}>
            Emitir licença
          </Button>
        }
      />

      <Toolbar>
        <form onSubmit={submitLookup} className="flex items-center gap-1">
          <SearchInput
            label="Buscar por texto da chave"
            placeholder="ATHN-XXXXX-XXXXX-XXXXX-XXXXX"
            value={lookupInput}
            onValueChange={setLookupInput}
            className="w-72"
          />
          <Button type="submit" size="sm">
            buscar chave
          </Button>
          {lookup !== '' && (
            <Button
              size="sm"
              onClick={() => {
                setLookup('')
                setLookupInput('')
              }}
            >
              limpar
            </Button>
          )}
        </form>

        {lookup === '' && (
          <>
            <label className="flex items-center gap-1 text-micro uppercase tracking-label text-fg-muted">
              status
              <select
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value as ProductKeyStatus | '')
                  setPage(1)
                }}
                className="border border-line-strong bg-inset px-1.5 py-0.5 text-sm text-fg"
              >
                <option value="">todos</option>
                {PRODUCT_KEY_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {STATUS_LABEL[option]}
                  </option>
                ))}
              </select>
            </label>

            <FilterToggle
              label="removidas"
              pressed={includeDeleted}
              onPressedChange={(value) => {
                setIncludeDeleted(value)
                setPage(1)
              }}
            />
          </>
        )}

        {filtered && (
          <Button size="sm" onClick={() => navigate('/licencas')}>
            filtro:{' '}
            {companyFilter !== ''
              ? (companyIndex.get(companyFilter)?.companyCode ?? 'empresa')
              : (machineIndex.get(machineFilter)?.fingerprint ?? 'máquina')}{' '}
            ✕
          </Button>
        )}

        {loading && <span className="text-micro text-fg-muted">carregando…</span>}
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-auto">
        {error !== null ? (
          <ErrorState error={error} onRetry={reload} />
        ) : data === null ? (
          <LoadingRow />
        ) : data.items.length === 0 ? (
          <EmptyState
            title="Nenhuma licença encontrada."
            action={<Button onClick={() => setDialog({ kind: 'create' })}>Emitir licença</Button>}
          />
        ) : (
          <table className={TABLE}>
            <thead className={THEAD}>
              <tr>
                <th scope="col" className={TH}>
                  Chave
                </th>
                <th scope="col" className={TH}>
                  Status
                </th>
                <th scope="col" className={TH}>
                  Empresa
                </th>
                <th scope="col" className={TH}>
                  Máquina
                </th>
                <th scope="col" className={TH}>
                  Autoriza
                </th>
                <th scope="col" className={TH}>
                  Última ativação
                </th>
                <th scope="col" className={`${TH} text-right`}>
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((key) => {
                const company = companyIndex.get(key.companyId)
                const machine = key.machineId === null ? undefined : machineIndex.get(key.machineId)

                return (
                  <tr key={key.id} className={TR}>
                    <th scope="row" className={`${TD} font-normal whitespace-nowrap`}>
                      <CopyValue value={key.productKey} label="chave da licença" />
                    </th>
                    <td className={TD}>
                      <span className="flex items-center gap-1">
                        <ProductKeyStatusBadge status={key.status} />
                        {key.deletedAt !== null && <Badge tone="crit">removida</Badge>}
                      </span>
                    </td>
                    <td className={`${TD} whitespace-nowrap`}>
                      {company === undefined ? (
                        <ShortId id={key.companyId} label="companyId" />
                      ) : (
                        <span className="font-mono text-fg">{company.companyCode}</span>
                      )}
                    </td>
                    <td className={`${TD} max-w-48 whitespace-nowrap`}>
                      {key.machineId === null ? (
                        <span className="text-fg-muted">sem vínculo</span>
                      ) : machine === undefined ? (
                        <ShortId id={key.machineId} label="machineId" />
                      ) : (
                        <span className="block truncate font-mono" title={machine.fingerprint}>
                          {machine.fingerprint}
                        </span>
                      )}
                    </td>
                    <td className={TD}>
                      {key.authorizesIngestion ? (
                        <Badge tone="ok" dot>
                          sim
                        </Badge>
                      ) : (
                        <Badge tone="neutral">não</Badge>
                      )}
                    </td>
                    <td className={`${TD} tnum whitespace-nowrap`}>
                      {formatDayClock(key.lastActivationAt)}
                    </td>
                    <td className={`${TD} text-right`}>
                      {key.deletedAt === null && (
                        <RowActions keyItem={key} onPick={(next) => setDialog(next)} />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {data !== null && data.items.length > 0 && lookup === '' && (
        <Pagination
          page={data.page}
          pageSize={data.pageSize}
          totalCount={data.totalCount}
          totalPages={data.totalPages}
          busy={loading}
          onPageChange={setPage}
        />
      )}

      {dialog?.kind === 'create' && (
        <CreateDialog
          companies={[...companyIndex.values()]}
          machines={[...machineIndex.values()]}
          preselectedCompanyId={companyFilter}
          preselectedMachineId={machineFilter}
          onClose={close}
          onCreated={refresh}
        />
      )}

      {dialog?.kind === 'activate' && (
        <ActivateDialog
          keyItem={dialog.key}
          machines={[...machineIndex.values()]}
          onClose={close}
          onDone={refresh}
        />
      )}

      {dialog?.kind === 'suspend' && (
        <SimpleActionDialog
          title="Suspender licença?"
          consequence="A ingestão desta máquina para na próxima conexão do agente. Reversível."
          confirmLabel="Suspender"
          success="Licença suspensa."
          task={() => productKeys.suspend(dialog.key.id)}
          onClose={close}
          onDone={refresh}
        />
      )}

      {dialog?.kind === 'revoke' && (
        <SimpleActionDialog
          title="Revogar licença?"
          consequence={
            <LicenseEndConsequence
              text="Definitivo. Religar exige emitir uma licença nova."
              keyItem={dialog.key}
              otherLiveKey={otherLiveKeyOf(dialog.key)}
            />
          }
          confirmLabel="Revogar em definitivo"
          success={licenseUninstallToast('Licença revogada.', dialog.key.machineId !== null, otherLiveKeyOf(dialog.key))}
          task={() => productKeys.revoke(dialog.key.id)}
          onClose={close}
          onDone={refresh}
        />
      )}

      {dialog?.kind === 'bind' && (
        <BindDialog
          keyItem={dialog.key}
          machines={[...machineIndex.values()]}
          onClose={close}
          onDone={refresh}
        />
      )}

      {dialog?.kind === 'unbind' && (
        <SimpleActionDialog
          title="Desvincular a máquina?"
          consequence="A licença deixa de autorizar qualquer máquina. O status é mantido."
          confirmLabel="Desvincular"
          success="Máquina desvinculada."
          // PUT com `machineId` EXPLÍCITO em null e o status atual preservado.
          // É a única forma de desvincular, e a razão de o campo ser obrigatório
          // no tipo: omiti-lo faria a mesma coisa sem que ninguém tivesse pedido.
          task={() =>
            productKeys.update(dialog.key.id, { machineId: null, status: dialog.key.status })
          }
          onClose={close}
          onDone={refresh}
        />
      )}

      {dialog?.kind === 'delete' && (
        <SimpleActionDialog
          title="Remover licença?"
          consequence={
            <LicenseEndConsequence
              text="Também revoga a licença. Definitivo."
              keyItem={dialog.key}
              otherLiveKey={otherLiveKeyOf(dialog.key)}
            />
          }
          confirmLabel="Remover"
          success={licenseUninstallToast('Licença removida.', dialog.key.machineId !== null, otherLiveKeyOf(dialog.key))}
          task={() => productKeys.remove(dialog.key.id)}
          onClose={close}
          onDone={refresh}
        />
      )}
    </>
  )
}

/**
 * As ações possíveis vêm da máquina de estados do servidor, não de uma lista
 * fixa: oferecer "suspender" numa licença revogada só produziria um 409 que o
 * operador não tinha como prever.
 */
function RowActions({
  keyItem,
  onPick,
}: {
  keyItem: ProductKeyResponse
  onPick: (dialog: Dialog) => void
}) {
  const { status } = keyItem
  const revoked = status === 'Revoked'

  return (
    <Menu trigger={<span>ações</span>} label={`Ações da licença ${keyItem.productKey}`}>
      {(status === 'Created' || status === 'Suspended' || status === 'Expired') && (
        <MenuItem onSelect={() => onPick({ kind: 'activate', key: keyItem })}>Ativar</MenuItem>
      )}
      {status === 'Active' && (
        <MenuItem onSelect={() => onPick({ kind: 'suspend', key: keyItem })}>Suspender</MenuItem>
      )}
      {!revoked && keyItem.machineId === null && (
        <MenuItem onSelect={() => onPick({ kind: 'bind', key: keyItem })}>
          Vincular máquina
        </MenuItem>
      )}
      {!revoked && keyItem.machineId !== null && status !== 'Active' && (
        <MenuItem onSelect={() => onPick({ kind: 'unbind', key: keyItem })}>
          Desvincular máquina
        </MenuItem>
      )}
      {!revoked && <MenuSeparator />}
      {!revoked && (
        <MenuItem danger onSelect={() => onPick({ kind: 'revoke', key: keyItem })}>
          Revogar
        </MenuItem>
      )}
      <MenuItem danger onSelect={() => onPick({ kind: 'delete', key: keyItem })}>
        Remover
      </MenuItem>
    </Menu>
  )
}

// ── índices de rótulo ───────────────────────────────────────────────────────

function useCompanyIndex(): Map<string, CompanyResponse> {
  const fetcher = useCallback(
    (signal: AbortSignal) => companies.list({ page: 1, pageSize: LABEL_LIMIT }, signal),
    [],
  )
  const { data } = useResource(fetcher)

  return useMemo(
    () => new Map((data?.items ?? []).map((company) => [company.id, company])),
    [data],
  )
}

function useMachineIndex(): Map<string, MachineResponse> {
  const fetcher = useCallback(
    (signal: AbortSignal) => machines.list({ page: 1, pageSize: LABEL_LIMIT }, signal),
    [],
  )
  const { data } = useResource(fetcher)

  return useMemo(
    () => new Map((data?.items ?? []).map((machine) => [machine.id, machine])),
    [data],
  )
}

const NO_MACHINE = ''

function machineOptions(list: readonly MachineResponse[]): Array<{ value: string; label: string }> {
  return [
    { value: NO_MACHINE, label: '— sem máquina —' },
    ...list
      .filter((machine) => machine.deletedAt === null)
      .map((machine) => ({ value: machine.id, label: machine.fingerprint })),
  ]
}

// ── diálogos ────────────────────────────────────────────────────────────────

function CreateDialog({
  companies: companyList,
  machines: machineList,
  preselectedCompanyId,
  preselectedMachineId,
  onClose,
  onCreated,
}: {
  companies: readonly CompanyResponse[]
  machines: readonly MachineResponse[]
  preselectedCompanyId: string
  preselectedMachineId: string
  onClose: () => void
  onCreated: () => void
}) {
  const toast = useToast()
  const action = useAction()
  const [companyId, setCompanyId] = useState(preselectedCompanyId)
  const [machineId, setMachineId] = useState(preselectedMachineId)
  const [activate, setActivate] = useState(false)
  const [issued, setIssued] = useState<ProductKeyResponse | null>(null)

  const field = fieldOfError(action.error)

  const companyOptions = [
    { value: '', label: '— escolha a empresa —' },
    ...companyList
      .filter((company) => company.deletedAt === null)
      .map((company) => ({
        value: company.id,
        label: `${company.companyCode} — ${company.name}${company.isActive ? '' : ' (desativada)'}`,
      })),
  ]

  // A chave só existe uma vez na tela: depois de fechar este diálogo ela é
  // recuperável pela listagem, mas é aqui que ela está pronta para ser copiada
  // e enviada ao cliente.
  if (issued !== null) {
    return (
      <ConfirmDialog
        open
        onOpenChange={(open) => {
          if (!open) {
            setIssued(null)
            onCreated()
          }
        }}
        title="Licença emitida"
        consequence={
          <span className="flex flex-col gap-2">
            <CopyValue value={issued.productKey} label="chave da licença" />
            <span>
              Status <span className="text-fg">{STATUS_LABEL[issued.status]}</span>.
            </span>
          </span>
        }
        confirmLabel="Concluir"
        destructive={false}
        onConfirm={() => {
          setIssued(null)
          onCreated()
        }}
      />
    )
  }

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Emitir licença"
      submitLabel="Emitir"
      busy={action.busy}
      error={field === null && action.error !== null ? describeError(action.error) : null}
      onSubmit={() =>
        action.run(async () => {
          const created = await productKeys.create({
            companyId,
            machineId: machineId === NO_MACHINE ? null : machineId,
            activate,
          })
          toast.success('Licença emitida.')
          setIssued(created)
        })
      }
    >
      <SelectField
        label="Empresa"
        value={companyId}
        onValueChange={setCompanyId}
        options={companyOptions}
        error={field === 'companyId' ? describeError(action.error) : null}
      />

      <SelectField
        label="Máquina"
        value={machineId}
        onValueChange={(value) => {
          setMachineId(value)
          if (value === NO_MACHINE) setActivate(false)
        }}
        options={machineOptions(machineList)}
        error={field === 'machineId' ? describeError(action.error) : null}
        hint="Opcional."
      />

      <CheckboxField
        label="Ativar na emissão"
        checked={activate}
        onCheckedChange={setActivate}
        disabled={machineId === NO_MACHINE}
        hint={machineId === NO_MACHINE ? 'Exige uma máquina.' : undefined}
      />
    </FormDialog>
  )
}

function ActivateDialog({
  keyItem,
  machines: machineList,
  onClose,
  onDone,
}: {
  keyItem: ProductKeyResponse
  machines: readonly MachineResponse[]
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const action = useAction()
  const [machineId, setMachineId] = useState(keyItem.machineId ?? NO_MACHINE)

  const field = fieldOfError(action.error)

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Ativar licença"
      submitLabel="Ativar"
      busy={action.busy}
      error={field === null && action.error !== null ? describeError(action.error) : null}
      onSubmit={() =>
        action.run(async () => {
          // `/activate` vincula e ativa num ato só; mandar o mesmo machineId que
          // já está lá é inofensivo e mantém o corpo explícito.
          await productKeys.activate(keyItem.id, {
            machineId: machineId === NO_MACHINE ? null : machineId,
          })
          toast.success('Licença ativada.')
          onDone()
        })
      }
    >
      <SelectField
        label="Máquina"
        value={machineId}
        onValueChange={setMachineId}
        options={machineOptions(machineList)}
        error={field === 'machineId' ? describeError(action.error) : null}
        hint="Uma máquina só pode ter uma licença ativa por vez."
      />

      {machineId === NO_MACHINE && (
        <Notice tone="warn">
          Sem máquina vinculada a ativação é recusada.
        </Notice>
      )}
    </FormDialog>
  )
}

function BindDialog({
  keyItem,
  machines: machineList,
  onClose,
  onDone,
}: {
  keyItem: ProductKeyResponse
  machines: readonly MachineResponse[]
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const action = useAction()
  const [machineId, setMachineId] = useState(NO_MACHINE)

  const field = fieldOfError(action.error)

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Vincular máquina"
      submitLabel="Vincular"
      busy={action.busy}
      error={field === null && action.error !== null ? describeError(action.error) : null}
      onSubmit={() =>
        action.run(async () => {
          // PUT com os DOIS campos explícitos. O status vai como está hoje
          // justamente para que vincular não mude estado sem ninguém ter pedido.
          await productKeys.update(keyItem.id, {
            machineId: machineId === NO_MACHINE ? null : machineId,
            status: keyItem.status,
          })
          toast.success('Máquina vinculada.')
          onDone()
        })
      }
    >
      <SelectField
        label="Máquina"
        value={machineId}
        onValueChange={setMachineId}
        options={machineOptions(machineList)}
        error={field === 'machineId' ? describeError(action.error) : null}
      />
    </FormDialog>
  )
}

/**
 * O que revogar ou remover a licença faz — inclusive com o agente da máquina
 * vinculada. O Athena emite a desinstalação sozinho; aqui só se avisa.
 */
export function LicenseEndConsequence({
  text,
  keyItem,
  otherLiveKey,
}: {
  text: string
  keyItem: ProductKeyResponse
  otherLiveKey: boolean | null
}) {
  const warning = licenseUninstallWarning(keyItem.machineId !== null, otherLiveKey)
  return (
    <>
      {text}
      {warning !== null && <UninstallWarning>{warning}</UninstallWarning>}
    </>
  )
}

/** Confirmação de uma ação sem parâmetro: suspender, revogar, remover, desvincular. */
function SimpleActionDialog({
  title,
  consequence,
  confirmLabel,
  success,
  task,
  onClose,
  onDone,
}: {
  title: string
  consequence: ReactNode
  confirmLabel: string
  success: string
  task: () => Promise<unknown>
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const action = useAction()

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={title}
      consequence={consequence}
      confirmLabel={confirmLabel}
      busy={action.busy}
      error={action.error !== null ? describeError(action.error) : null}
      onConfirm={() =>
        action.run(async () => {
          await task()
          toast.success(success)
          onDone()
        })
      }
    />
  )
}
